/**
 * Test Runner UI Page
 * Visual interface for running and monitoring automated tests
 * Includes publish gate that blocks deployment if core tests fail
 */

import { useState } from 'react';
import { Play, CheckCircle2, XCircle, AlertCircle, Clock, ShieldAlert, ShieldCheck, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { runAllTests } from '@/tests/automated-test-runner';
import { expenseParsingTests } from '@/tests/expense-parsing-tests';
import { chatTests } from '@/tests/chat-tests';

interface TestProgress {
  current: number;
  total: number;
  currentTest: string;
}

interface TestStats {
  passed: number;
  failed: number;
  total: number;
  avgTime: number;
  passRate: number;
}

interface FailedTest {
  id: string;
  input: string;
  reason: string;
  category: string;
}

interface PublishGateStatus {
  canPublish: boolean;
  failedTests: FailedTest[];
  lastRunAt: Date | null;
  totalTests: number;
  passedTests: number;
}

export default function TestRunner() {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<TestProgress>({ current: 0, total: 0, currentTest: '' });
  const [expenseStats, setExpenseStats] = useState<TestStats | null>(null);
  const [chatStats, setChatStats] = useState<TestStats | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [publishGate, setPublishGate] = useState<PublishGateStatus>({
    canPublish: false,
    failedTests: [],
    lastRunAt: null,
    totalTests: 0,
    passedTests: 0
  });

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const runTests = async (type: 'all' | 'expense' | 'chat') => {
    setIsRunning(true);
    setLogs([]);
    
    const expenseCount = expenseParsingTests.length;
    const chatCount = chatTests.length;
    const total = type === 'all' ? expenseCount + chatCount : 
                  type === 'expense' ? expenseCount : chatCount;
    
    setProgress({ current: 0, total, currentTest: 'Starting...' });
    addLog(`🧪 Starting ${type} tests...`);

    // Intercept console.log to capture test output
    const originalLog = console.log;
    const capturedLogs: any[] = [];
    
    console.log = (...args) => {
      capturedLogs.push(args);
      originalLog(...args);
    };

    try {
      await runAllTests({
        expenseTests: type === 'all' || type === 'expense',
        chatTests: type === 'all' || type === 'chat'
      });

      // Parse results from captured logs
      const results = capturedLogs.join('\n');
      
      let totalPassed = 0;
      let totalTests = 0;
      const failedTests: FailedTest[] = [];
      
      // Extract expense parsing stats
      if (type === 'all' || type === 'expense') {
        const expenseMatch = results.match(/Expense Parsing.*?Passed: (\d+)\/(\d+).*?avg: (\d+)ms/s);
        if (expenseMatch) {
          const [, passed, total, avgTime] = expenseMatch;
          const passedNum = parseInt(passed);
          const totalNum = parseInt(total);
          totalPassed += passedNum;
          totalTests += totalNum;
          
          setExpenseStats({
            passed: passedNum,
            failed: totalNum - passedNum,
            total: totalNum,
            avgTime: parseInt(avgTime),
            passRate: (passedNum / totalNum) * 100
          });
          
          // Extract failed test IDs and reasons from logs
          const failedTestPattern = /❌ (test-\d+).*?Input: "([^"]+)".*?(?:Errors?:|Mismatch:)\s*([^\n]+)/gs;
          let match;
          while ((match = failedTestPattern.exec(results)) !== null) {
            const testCase = expenseParsingTests.find(t => t.id === match[1]);
            failedTests.push({
              id: match[1],
              input: match[2],
              reason: match[3].trim(),
              category: testCase?.category || 'Unknown'
            });
          }
        }
      }

      // Extract chat stats
      if (type === 'all' || type === 'chat') {
        const chatMatch = results.match(/Chat Interaction.*?Passed: (\d+)\/(\d+).*?avg: (\d+)ms/s);
        if (chatMatch) {
          const [, passed, total, avgTime] = chatMatch;
          const passedNum = parseInt(passed);
          const totalNum = parseInt(total);
          totalPassed += passedNum;
          totalTests += totalNum;
          
          setChatStats({
            passed: passedNum,
            failed: totalNum - passedNum,
            total: totalNum,
            avgTime: parseInt(avgTime),
            passRate: (passedNum / totalNum) * 100
          });
          
          // Extract failed chat test IDs
          const failedChatPattern = /❌ (chat-\d+).*?Scenario: "([^"]+)".*?(?:Errors?:|Mismatch:)\s*([^\n]+)/gs;
          let match;
          while ((match = failedChatPattern.exec(results)) !== null) {
            const testCase = chatTests.find(t => t.id === match[1]);
            failedTests.push({
              id: match[1],
              input: match[2],
              reason: match[3].trim(),
              category: testCase?.category || 'Unknown'
            });
          }
        }
      }

      // Update publish gate status
      const canPublish = totalTests > 0 && totalPassed === totalTests;
      setPublishGate({
        canPublish,
        failedTests,
        lastRunAt: new Date(),
        totalTests,
        passedTests: totalPassed
      });

      if (canPublish) {
        addLog('✅ All tests passed! Safe to publish.');
      } else {
        addLog(`⚠️ ${failedTests.length} test(s) failed. Publishing blocked.`);
      }
    } catch (error) {
      addLog(`❌ Error running tests: ${error instanceof Error ? error.message : String(error)}`);
      setPublishGate(prev => ({
        ...prev,
        canPublish: false,
        lastRunAt: new Date()
      }));
    } finally {
      console.log = originalLog;
      setIsRunning(false);
      setProgress({ current: total, total, currentTest: 'Complete' });
    }
  };

  return (
    <div className="container max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">🧪 Automated Test Suite</h1>
        <p className="text-muted-foreground">
          Comprehensive testing for expense parsing and chat functionality
        </p>
      </div>

      {/* Publish Gate Status */}
      {publishGate.lastRunAt && (
        <Alert variant={publishGate.canPublish ? "default" : "destructive"} className={publishGate.canPublish ? "border-primary bg-primary/5" : ""}>
          <div className="flex items-start gap-3">
            {publishGate.canPublish ? (
              <ShieldCheck className="h-5 w-5 text-primary" />
            ) : (
              <ShieldAlert className="h-5 w-5" />
            )}
            <div className="flex-1">
              <AlertTitle className="flex items-center gap-2">
                {publishGate.canPublish ? (
                  <>
                    <span>Ready to Publish</span>
                    <Rocket className="h-4 w-4" />
                  </>
                ) : (
                  'Publishing Blocked'
                )}
              </AlertTitle>
              <AlertDescription className="mt-2">
                {publishGate.canPublish ? (
                  <p className="text-sm">
                    All {publishGate.totalTests} core tests passed. 
                    <span className="text-muted-foreground ml-2">
                      Last run: {publishGate.lastRunAt.toLocaleTimeString()}
                    </span>
                  </p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm">
                      {publishGate.passedTests}/{publishGate.totalTests} tests passed. 
                      Fix the following {publishGate.failedTests.length} failing test(s) before publishing:
                    </p>
                    <div className="space-y-2 mt-3">
                      {publishGate.failedTests.map((test) => (
                        <div key={test.id} className="bg-destructive/10 rounded-md p-3 text-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="destructive" className="font-mono text-xs">{test.id}</Badge>
                            <Badge variant="outline" className="text-xs">{test.category}</Badge>
                          </div>
                          <p className="font-mono text-xs text-muted-foreground truncate" title={test.input}>
                            Input: "{test.input.length > 50 ? test.input.slice(0, 50) + '...' : test.input}"
                          </p>
                          <p className="text-xs mt-1 text-destructive-foreground">
                            <XCircle className="h-3 w-3 inline mr-1" />
                            {test.reason}
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Last run: {publishGate.lastRunAt.toLocaleTimeString()}
                    </p>
                  </div>
                )}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {/* Test Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Test Controls</CardTitle>
          <CardDescription>Run automated tests to verify functionality before publishing</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-3 flex-wrap">
          <Button
            onClick={() => runTests('all')}
            disabled={isRunning}
            className="flex-1 min-w-[200px]"
          >
            <Play className="w-4 h-4 mr-2" />
            Run All Tests ({expenseParsingTests.length + chatTests.length})
          </Button>
          <Button
            onClick={() => runTests('expense')}
            disabled={isRunning}
            variant="outline"
            className="flex-1 min-w-[150px]"
          >
            Run Expense Tests ({expenseParsingTests.length})
          </Button>
          <Button
            onClick={() => runTests('chat')}
            disabled={isRunning}
            variant="outline"
            className="flex-1 min-w-[150px]"
          >
            Run Chat Tests ({chatTests.length})
          </Button>
        </CardContent>
      </Card>

      {/* Progress */}
      {isRunning && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Running: {progress.currentTest}</span>
                <span>{progress.current} / {progress.total}</span>
              </div>
              <Progress value={(progress.current / progress.total) * 100} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="expense">Expense Tests</TabsTrigger>
          <TabsTrigger value="chat">Chat Tests</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {expenseStats && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Expense Parsing Tests
                  </CardTitle>
                  {expenseStats.failed === 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{expenseStats.passRate.toFixed(1)}%</div>
                  <p className="text-xs text-muted-foreground">
                    {expenseStats.passed} / {expenseStats.total} passed
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm">
                      <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                      Avg: {expenseStats.avgTime}ms
                    </div>
                    {expenseStats.failed > 0 && (
                      <div className="flex items-center text-sm text-destructive">
                        <XCircle className="w-4 h-4 mr-2" />
                        {expenseStats.failed} failed
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {chatStats && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Chat Interaction Tests
                  </CardTitle>
                  {chatStats.failed === 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{chatStats.passRate.toFixed(1)}%</div>
                  <p className="text-xs text-muted-foreground">
                    {chatStats.passed} / {chatStats.total} passed
                  </p>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm">
                      <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                      Avg: {chatStats.avgTime}ms
                    </div>
                    {chatStats.failed > 0 && (
                      <div className="flex items-center text-sm text-destructive">
                        <XCircle className="w-4 h-4 mr-2" />
                        {chatStats.failed} failed
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Test Logs</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] w-full rounded border p-4">
                <div className="space-y-1 font-mono text-sm">
                  {logs.length === 0 ? (
                    <p className="text-muted-foreground">No logs yet. Run tests to see output.</p>
                  ) : (
                    logs.map((log, i) => (
                      <div key={i} className="text-xs">{log}</div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expense" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Expense Parsing Test Cases</CardTitle>
              <CardDescription>{expenseParsingTests.length} test cases covering various input formats</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {expenseParsingTests.map((test) => (
                    <div key={test.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Badge variant="outline" className="mb-1">{test.category}</Badge>
                          <p className="font-mono text-sm">{test.input}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{test.notes}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chat" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Chat Interaction Test Cases</CardTitle>
              <CardDescription>{chatTests.length} test scenarios for chat functionality</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {chatTests.map((test) => (
                    <div key={test.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <Badge variant="outline" className="mb-1">{test.category}</Badge>
                          <p className="text-sm font-medium mb-2">{test.scenario}</p>
                          <div className="space-y-1">
                            {test.userMessages.map((msg, i) => (
                              <p key={i} className="font-mono text-xs text-muted-foreground">
                                User: {msg}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{test.notes}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
